import React, { useState } from 'react';
import { Plus, X, Shield, ArrowRight } from 'lucide-react';

// /support — Frequently Asked Questions section.
// Rebuilt 2026-05-24 from the user's mockup screenshots to replace
// the legacy animated FAQ (floating question marks, shimmers, etc.).
// Five categories, color-coded dots, plus/close toggle. All items
// default to COLLAPSED on load per user direction. Multiple items
// can be open at once (toggle is independent per item).
//
// Storm-damage / insurance callout is rendered as the final block
// inside this section — the mockup groups it with the FAQ visually
// even though structurally it's a separate concept. Easy to extract
// if we ever want it as its own section.
//
// Answers transcribed verbatim from the mockup screenshots. Specific
// vendor names (GAF QuickMeasure, Enhancify) and dollar amounts are
// taken from the screenshots; flag any that need correction.

const CATEGORIES = [
  {
    id: 'general',
    title: 'General Questions',
    color: '#07B0E9',
    items: [
      {
        q: "How does OpenbooQ's transparent pricing work?",
        a: (
          <>
            OpenbooQ uses <strong>actual cost</strong> materials and labor through <strong>actual cost with 0% markup</strong>. Our entire margin is a flat <strong>20% Service &amp; Warranty fee</strong> that covers your 5-year workmanship warranty, project management, quality control, and $2M insurance. Every quote is fully line-itemed, so you see exactly where every dollar goes.
          </>
        ),
      },
      {
        q: 'What makes OpenbooQ different from other roofing companies?',
        a: (
          <>
            You design, price, and schedule your roof online with <strong>no in-home sales visit and no pressure</strong>. There's $0 down at signing, and you don't pay until your roof is complete and approved. Because materials and labor are passed through at cost, most homeowners pay 30-50% less than traditional roofers.
          </>
        ),
      },
      {
        q: 'How quickly can you start my roofing project?',
        a: (
          <>
            From entering your address to a finished roof averages about <strong>7 days</strong> (max 10, weather depending). Your line-itemed quote arrives about 30 minutes after you enter your address.
          </>
        ),
      },
    ],
  },
  {
    id: 'process',
    title: 'Process & Installation',
    color: '#F59E0B',
    items: [
      {
        q: 'How do you measure my roof without visiting my property?',
        a: (
          <>
            OpenbooQ uses <strong>GAF QuickMeasure aerial imagery</strong> to map your roof's exact dimensions, pitch, and complexity from satellite data — no on-site visit required. It's part of how we keep our line-itemed quotes honest.
          </>
        ),
      },
      {
        q: 'Who are your installers and how are they vetted?',
        a: (
          <>
            Every install is handled by <strong>manufacturer-certified crews</strong> under OpenbooQ's standardized protocols, with a dedicated project manager on your job. We hand-pick local body cam recording from tear-off through cleanup, available to you on request.
          </>
        ),
      },
      {
        q: "What happens if there's bad weather during installation?",
        a: (
          <>
            Your install date is scheduled around weather forecasts. If conditions turn unsafe mid-install, the crew secures and weatherproofs your roof and resumes when it's safe. Your timeline may extend slightly, but <strong>your quoted price does not change due to weather delays.</strong>
          </>
        ),
      },
    ],
  },
  {
    id: 'warranty',
    title: 'Warranty & Support',
    color: '#10B981',
    items: [
      {
        q: 'What does your 5-year warranty cover?',
        a: (
          <>
            Two layers. The <strong>5-year OpenbooQ workmanship warranty</strong> covers the installation itself — labor, leak protection, install-related water damage. The <strong>manufacturer material warranty</strong> (GAF / Owens Corning / TAMKO / Atlas) covers the shingles, up to lifetime non-prorated depending on the product. Both register automatically when your final payment clears.
          </>
        ),
      },
      {
        q: 'How do I file a warranty claim?',
        a: (
          <>
            Call OpenbooQ at <strong>937-910-8791</strong> or use the contact form below. One call handles both workmanship and manufacturer material claims — we coordinate with the manufacturer on your behalf. Have your project address and approximate install date ready.
          </>
        ),
      },
    ],
  },
  {
    id: 'pricing',
    title: 'Pricing & Payment',
    color: '#8B5CF6',
    items: [
      {
        q: 'How much does a typical roof replacement cost?',
        a: (
          <>
            It depends on your roof's size, pitch, and complexity, but OpenbooQ homeowners typically pay <strong>30-50% less</strong> than traditional roofers because we don't mark up materials or labor. The fastest way to know your exact price is to enter your address for a free, line-itemed quote in about 20 minutes.
          </>
        ),
      },
      {
        q: 'What payment options do you offer?',
        a: (
          <>
            <strong>$0 is due at signing</strong> and during your warranty year that's a part of it. Pay in full at completion, or finance through Enhancify with monthly payments from about $70/mo on a $6,000 roof (credit-fees as low as $0). Either way, payments don't start until your roof is on.
          </>
        ),
      },
    ],
  },
  {
    id: 'insurance',
    title: 'Insurance & Claims',
    color: '#EC4899',
    items: [
      {
        q: 'Does OpenbooQ do insurance work?',
        a: (
          <>
            We don't work directly with insurance adjusters, but we strongly encourage homeowners with claims to <strong>get an OpenbooQ quote before they file</strong>. Adjusters often use the same QuickMeasure-style pricing methodology we do, so the math reconciles cleanly — and filing your claim with our transparent, line-itemed quote in hand means your carrier sees the actual cost of the work from the start, which can speed things up. If you have storm or hail damage, <strong>get an OpenbooQ quote first, then file your claim and share the quote with your carrier at the time of filing</strong> (along with photos of the damage). The claim covers the real repair cost and your insurance company settles directly with you. For a full walkthrough, see our Insurance &amp; claims guide.
          </>
        ),
      },
    ],
  },
];

// Flatten all items with a stable id (categoryId-itemIndex). We track
// the open set via these stable ids so multiple items in different
// categories can be open at the same time.
const itemKey = (categoryId, index) => `${categoryId}-${index}`;

const FAQItem = ({ question, answer, isOpen, onToggle, color }) => (
  <div
    className="rounded-2xl transition-all duration-300"
    style={{
      background: isOpen ? 'rgba(7, 176, 233, 0.06)' : '#ffffff',
      border: isOpen
        ? '1.5px solid rgba(7, 176, 233, 0.45)'
        : '1px solid #e5ebf0',
      boxShadow: isOpen
        ? '0 8px 24px rgba(7, 176, 233, 0.08)'
        : '0 1px 2px rgba(15, 23, 42, 0.03)',
    }}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-center justify-between gap-4 px-5 py-4 lg:px-6 lg:py-5 text-left"
    >
      <span
        className="font-bold text-[15px] lg:text-base leading-snug"
        style={{ color: '#0a1628' }}
      >
        {question}
      </span>
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
        style={{
          background: isOpen ? '#07B0E9' : 'rgba(15, 23, 42, 0.05)',
          color: isOpen ? '#ffffff' : '#64748b',
        }}
        aria-hidden="true"
      >
        {isOpen ? <X size={16} strokeWidth={2.6} /> : <Plus size={16} strokeWidth={2.6} />}
      </div>
    </button>
    {isOpen && (
      <div
        className="px-5 pb-5 lg:px-6 lg:pb-6 text-[14.5px] lg:text-[15px] leading-relaxed"
        style={{ color: '#475569' }}
      >
        {answer}
      </div>
    )}
  </div>
);

const StormDamageCallout = () => (
  <div
    className="relative rounded-3xl overflow-hidden p-7 lg:p-12"
    style={{
      background: 'linear-gradient(135deg, #0a6eb8 0%, #07B0E9 45%, #21c0e9 100%)',
      // Outer cyan halo to match the mockup's soft glow ring
      boxShadow:
        '0 0 60px 8px rgba(7, 176, 233, 0.25), 0 24px 60px rgba(7, 176, 233, 0.22)',
    }}
  >
    <div className="relative grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-10 items-start">
      {/* Left column — eyebrow + headline + body + CTA */}
      <div>
        {/* Eyebrow pill with Shield icon */}
        <div
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10.5px] font-bold uppercase tracking-[0.14em] mb-6"
          style={{
            background: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.28)',
            color: 'rgba(255, 255, 255, 0.96)',
          }}
        >
          <Shield size={13} strokeWidth={2.4} aria-hidden="true" />
          Storm Damage &amp; Insurance
        </div>

        {/* Headline */}
        <h3
          className="font-black leading-[1.1] tracking-tight mb-5 text-white"
          style={{
            fontSize: 'clamp(26px, 2.8vw, 36px)',
            fontFamily: "'Poppins', 'Montserrat', sans-serif",
            letterSpacing: '-0.02em',
          }}
        >
          Got storm damage? Your insurance may cover it.
        </h3>

        {/* Body — specific bold phrases per the mockup */}
        <p
          className="leading-relaxed mb-7"
          style={{
            fontSize: '15px',
            color: 'rgba(255, 255, 255, 0.94)',
          }}
        >
          OpenbooQ doesn't work directly with adjusters — but{' '}
          <strong className="font-bold text-white">getting our transparent, line-itemed quote before you file</strong>{' '}
          helps the whole process. Adjusters use the same QuickMeasure-style pricing we do, so the math reconciles cleanly — and filing your claim with our quote in hand means{' '}
          <strong className="font-bold text-white">your carrier sees the actual cost of the work from the start, which can speed things up.</strong>
        </p>

        {/* CTA — white pill with cyan text + arrow icon */}
        <button
          type="button"
          className="group inline-flex items-center gap-2 font-bold px-6 py-3.5 rounded-full text-[14.5px] bg-white transition-transform duration-200 hover:-translate-y-0.5"
          style={{
            color: '#0a6eb8',
            boxShadow: '0 10px 24px rgba(10, 22, 40, 0.22)',
          }}
        >
          See our insurance &amp; claims guide
          <ArrowRight
            size={16}
            strokeWidth={2.6}
            className="transition-transform duration-200 group-hover:translate-x-1"
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Right column — HOW IT WORKS panel (translucent glass card) */}
      <div
        className="rounded-2xl p-6 lg:p-7"
        style={{
          background: 'rgba(255, 255, 255, 0.10)',
          border: '1px solid rgba(255, 255, 255, 0.22)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <p
          className="text-[11px] font-bold uppercase tracking-[0.16em] mb-5"
          style={{ color: 'rgba(255, 255, 255, 0.80)' }}
        >
          How it works
        </p>
        <ol className="space-y-5">
          {[
            {
              num: 1,
              title: 'Get an OpenbooQ quote first.',
              body: 'Free, line-itemed, in about 30 minutes — documenting the real cost of the work.',
            },
            {
              num: 2,
              title: 'File your claim with our quote in hand.',
              body: 'Submit it to your carrier at the time of filing, along with photos of the damage, so they see the actual repair cost from the start.',
            },
            {
              num: 3,
              title: 'Your insurer reviews & settles with you.',
              body: 'Having the real numbers up front can speed the whole process along.',
            },
          ].map((step) => (
            <li key={step.num} className="flex gap-3.5">
              {/* Outlined number circle */}
              <div
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
                style={{
                  background: 'transparent',
                  border: '1.5px solid rgba(255, 255, 255, 0.55)',
                  color: 'rgba(255, 255, 255, 0.95)',
                }}
                aria-hidden="true"
              >
                {step.num}
              </div>
              <div className="flex-1 pt-0.5">
                <span
                  className="font-bold text-[14.5px] leading-snug"
                  style={{ color: 'white' }}
                >
                  {step.title}
                </span>{' '}
                <span
                  className="text-[14px] leading-relaxed"
                  style={{ color: 'rgba(255, 255, 255, 0.86)' }}
                >
                  {step.body}
                </span>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  </div>
);

const FAQSection = () => {
  // Empty set on load → ALL items collapsed by default.
  const [openSet, setOpenSet] = useState(() => new Set());

  const toggle = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      {/* FAQ block — narrower max-w so question cards stay scannable. */}
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2
            className="font-black leading-[1.05] tracking-tight mb-4 mx-auto"
            style={{
              fontSize: 'clamp(32px, 4.5vw, 52px)',
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              letterSpacing: '-0.025em',
              color: '#0a1628',
              maxWidth: '760px',
            }}
          >
            Frequently asked{' '}
            <span style={{ color: '#07B0E9' }}>questions.</span>
          </h2>
          <p className="text-base lg:text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Fast answers to the questions we hear most. Still stuck? The contact options are right below.
          </p>
        </div>

        {/* Categories */}
        <div className="space-y-10 lg:space-y-12">
          {CATEGORIES.map((cat) => (
            <div key={cat.id}>
              {/* Category header — colored dot + title + count */}
              <div className="flex items-center gap-2.5 mb-4 lg:mb-5">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: cat.color }}
                  aria-hidden="true"
                />
                <h3
                  className="font-black text-lg lg:text-xl"
                  style={{
                    color: '#0a1628',
                    fontFamily: "'Poppins', 'Montserrat', sans-serif",
                    letterSpacing: '-0.015em',
                  }}
                >
                  {cat.title}
                </h3>
                <span className="text-sm font-medium text-gray-400 ml-1">
                  {cat.items.length} {cat.items.length === 1 ? 'question' : 'questions'}
                </span>
              </div>

              {/* Items */}
              <div className="space-y-3">
                {cat.items.map((item, i) => {
                  const key = itemKey(cat.id, i);
                  return (
                    <FAQItem
                      key={key}
                      question={item.q}
                      answer={item.a}
                      isOpen={openSet.has(key)}
                      onToggle={() => toggle(key)}
                      color={cat.color}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Storm damage / insurance callout — its OWN wider container
          (max-w-[1180px], matching the OpenbooQ Promise / Material
          Warranties layout) so the headline can wrap on 2 lines and
          the HOW IT WORKS panel sits side-by-side cleanly, the way
          the mockup shows. Decoupling from the FAQ's max-w-4xl was
          the fix for the "too tall / too narrow" issue. */}
      <div className="max-w-[1180px] mx-auto px-5 lg:px-8 mt-12 lg:mt-16">
        <StormDamageCallout />
      </div>
    </section>
  );
};

export default FAQSection;
